import { useState, useEffect } from 'react'
import { Lock, Globe, Plus, ShieldAlert, X, Copy, Check, FileText, Upload, Download, Eye, Crown, ArrowLeft, Users, Clock, TrendingUp } from 'lucide-react'
import { formatDate } from '../lib/format'
import { vaultApi, documentsApi } from '../lib/api'
import { useToast } from '../lib/ToastContext'

// ✅ NEW - Investor components
import TokenCard from '../components/investor/TokenCard'
import InvestorStats from '../components/investor/InvestorStats'
import VisibilityToggle from '../components/investor/VisibilityToggle'
import { Card, Button, Badge, EmptyState } from '../components/ui'

const MODES = [
  {
    id: 'stealth',
    title: 'Stealth Mode (Private Vault)',
    icon: Lock,
    desc: 'Your business stays entirely under the radar. Access is strictly limited to stakeholders you manually invite via secure, time-limited tokens.',
  },
  {
    id: 'public',
    title: 'Publicly Listed (Investor Directory)',
    icon: Globe,
    desc: 'Broadcast your verified startup to a curated network of global venture capitalists and angel investors.',
  },
]

export default function InvestorPortal({ compliance, profileComplete, isPremium, onUpgrade, companyId, onNavigate }) {
  const [mode, setMode] = useState('stealth')
  const [complianceAlert, setComplianceAlert] = useState(false)
  const [tokens, setTokens] = useState([])
  const [copied, setCopied] = useState(null)
  const [docs, setDocs] = useState([])
  const [isPublic, setIsPublic] = useState(false)
  const { toast } = useToast()

  // Calculate stats
  const stats = {
    totalTokens: tokens.length,
    activeTokens: tokens.filter(t => t.status !== 'revoked' && new Date(t.expires) > new Date()).length,
    totalViews: tokens.reduce((sum, t) => sum + (t.viewCount || 0), 0),
    lastAccess: tokens.length > 0 ? tokens[0]?.lastAccessed || 'Never' : 'Never',
  }

  useEffect(() => {
    if (!companyId) return
    let mounted = true
    ;(async () => {
      const { data, error } = await vaultApi.listTokens(companyId)
      if (!mounted || error || !data) return
      setTokens(data.map((t) => ({
        id: t.token,
        recipient: t.investor_email,
        scope: 'view_summary',
        created: (t.created_at || '').split('T')[0],
        expires: (t.expires_at || '').split('T')[0],
        lastAccessed: t.last_accessed ? formatDate(t.last_accessed) : '—',
        status: t.status || 'active',
        viewCount: t.view_count || 0,
      })))
      const { data: docsData } = await documentsApi.list(companyId)
      if (!mounted || !docsData) return
      setDocs(docsData.map((d) => ({
        id: d.id,
        label: d.label,
        fileName: d.file_name || '',
        uploaded: !!d.file_url,
        status: d.status,
      })))
    })()
    return () => { mounted = false }
  }, [companyId])

  const allCompliant = compliance?.every((c) => c.status === 'ok') && tokens.length > 0

  const handleToggle = (next) => {
    if (next === 'public' && !allCompliant) {
      setMode('stealth')
      setComplianceAlert(true)
      return
    }
    setMode(next)
    setComplianceAlert(false)
  }

  const handleGenerate = async () => {
    if (!isPremium) {
      onUpgrade?.()
      return
    }
    if (!profileComplete) return
    if (companyId) {
      const email = prompt('Enter investor email:')
      if (!email) return
      const { data, error } = await vaultApi.createToken(companyId, email, 'Investor')
      if (error) { toast('Failed to generate token: ' + error, 'error'); return }
      setTokens((prev) => [
        { 
          id: data.token, 
          recipient: email, 
          scope: 'view_summary', 
          created: new Date().toISOString().slice(0, 10), 
          expires: (data.expires_at || '').split('T')[0], 
          lastAccessed: '—',
          status: 'active',
          viewCount: 0,
        },
        ...prev,
      ])
      toast('Vault token generated.', 'success')
    }
  }

  const copyToken = (id) => {
    navigator.clipboard?.writeText(id).catch(() => {})
    setCopied(id)
    setTimeout(() => setCopied(null), 1500)
  }
  
  const revokeToken = async (id) => {
    setTokens((prev) => prev.filter((t) => t.id !== id))
    if (companyId) await vaultApi.revokeToken(id)
    toast('Token revoked.', 'info')
  }

  const handleDocAction = (docId, action) => {
    if (action === 'upload') {
      setDocs((prev) => prev.map((d) => (d.id === docId ? { ...d, uploaded: true } : d)))
    }
  }

  const handleVisibilityToggle = (newState) => {
    setIsPublic(newState)
    toast(newState ? 'Company is now publicly visible.' : 'Company is now private.', 'success')
  }

  return (
    <div className="p-4 sm:p-8 lg:p-10 space-y-8 animate-fade-in">
      {/* Header */}
      <div>
        {/* {onNavigate && (
          <button onClick={() => onNavigate('dashboard')} className="btn-ghost text-xs flex items-center gap-1.5 mb-4">
            <ArrowLeft size={14} /> Back to Dashboard
          </button>
        )} */}
        <h1 className="text-2xl font-bold text-ink">Investor Portal</h1>
        <p className="text-sm text-muted mt-1">Manage access tokens and capital-raise visibility.</p>
      </div>

      {/* Profile Complete Warning */}
      {!profileComplete && (
        <div className="border border-amber-200 bg-amber-50 p-4 flex items-center gap-3 rounded-lg">
          <ShieldAlert size={16} className="text-amber-600 shrink-0" />
          <p className="text-sm text-amber-800 flex-1">
            Complete your profile first to unlock investor access and token generation.
          </p>
        </div>
      )}

      {/* ✅ NEW - Investor Stats */}
      <InvestorStats stats={stats} />

      {/* ✅ NEW - Visibility Toggle */}
      <VisibilityToggle
        isPublic={isPublic}
        onToggle={handleVisibilityToggle}
        onConfirm={handleVisibilityToggle}
      />

      {/* Capital Raise & Visibility Settings */}
      <section className="border border-neutral-200 rounded-xl p-7 bg-white">
        <h2 className="text-base font-semibold text-ink">Capital Raise & Visibility Settings</h2>
        <p className="text-sm text-muted mt-1 mb-6">Choose how your verified business appears to the investor network.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {MODES.map((m) => {
            const Icon = m.icon
            const active = mode === m.id
            return (
              <button
                key={m.id}
                onClick={() => handleToggle(m.id)}
                className={`relative text-left p-5 border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand rounded-lg ${
                  active ? 'border-brand bg-brand-soft' : 'border-neutral-200 bg-white hover:border-neutral-300'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2.5">
                    <Icon size={18} className={active ? 'text-brand' : 'text-neutral-400'} />
                    <span className={`text-sm font-semibold ${active ? 'text-brand' : 'text-ink'}`}>{m.title}</span>
                  </div>
                  <ToggleSwitch on={active} />
                </div>
                <p className="text-xs text-muted leading-relaxed">{m.desc}</p>
              </button>
            )
          })}
        </div>

        {complianceAlert && (
          <div className="mt-5 flex items-start gap-3 border border-rose-200 bg-rose-50 px-4 py-3 rounded-lg animate-fade-in">
            <ShieldAlert size={18} className="text-rose-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-rose-800 font-medium">Profile must be 100% compliant before listing publicly.</p>
              <p className="text-xs text-rose-700 mt-0.5">Please complete your documentation pipeline.</p>
            </div>
            <button onClick={() => setComplianceAlert(false)} className="text-rose-400 hover:text-rose-700"><X size={15} /></button>
          </div>
        )}
      </section>

      {/* ✅ NEW - Token Management with TokenCard */}
      <section className="border border-neutral-200 rounded-xl bg-white overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div>
            <h3 className="text-sm font-semibold text-ink flex items-center gap-2">
              Access Tokens
              {!isPremium && <Crown size={13} className="text-brand" />}
            </h3>
            <p className="text-xs text-muted mt-0.5">
              {tokens.length} active · last accessed {tokens[0]?.lastAccessed || 'Never'}
            </p>
          </div>
          <Button 
            variant="primary" 
            size="sm"
            onClick={handleGenerate}
            disabled={!isPremium || !profileComplete}
          >
            {isPremium ? (
              <><Plus size={14} /> Generate Token</>
            ) : (
              <><Crown size={14} /> Upgrade to Generate</>
            )}
          </Button>
        </div>

        {/* ✅ NEW - Token Cards */}
        <div className="divide-y divide-neutral-200">
          {tokens.length > 0 ? (
            tokens.map((t) => (
              <TokenCard
                key={t.id}
                token={t}
                onCopy={copyToken}
                onRevoke={revokeToken}
                copied={copied === t.id}
              />
            ))
          ) : (
            <EmptyState
              title="No active tokens"
              description="Generate a token to grant investor access to your company vault."
              className="py-12"
            />
          )}
        </div>
      </section>

      {/* Investor Documents */}
      <section>
        <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-neutral-500 mb-4">Investor Documents</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {docs.map((doc) => (
            <div key={doc.id} className="border border-neutral-200 rounded-xl p-5 flex flex-col bg-white hover:shadow-md transition-all duration-200">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-9 h-9 border border-neutral-200 bg-neutral-50 rounded-lg flex items-center justify-center shrink-0">
                  <FileText size={15} className="text-neutral-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-ink leading-tight">{doc.label}</p>
                  {doc.uploaded ? (
                    <Badge variant="success" size="sm" className="mt-1">
                      Uploaded
                    </Badge>
                  ) : (
                    <Badge variant="default" size="sm" className="mt-1">
                      Missing
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex gap-1 mt-auto pt-4 border-t border-neutral-200">
                <button onClick={() => handleDocAction(doc.id, 'upload')} className="btn-ghost text-xs flex items-center gap-1 flex-1 justify-center">
                  <Upload size={12} /> Upload
                </button>
                <button disabled={!doc.uploaded} className="btn-ghost text-xs flex items-center gap-1 flex-1 justify-center disabled:opacity-30" title="View">
                  <Eye size={12} /> View
                </button>
                <button disabled={!doc.uploaded} className="btn-ghost text-xs flex items-center gap-1 flex-1 justify-center disabled:opacity-30" title="Download">
                  <Download size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}

function ToggleSwitch({ on }) {
  return (
    <span className={`relative inline-flex h-5 w-9 items-center transition-colors duration-200 rounded-full ${on ? 'bg-brand' : 'bg-neutral-300'}`}>
      <span className={`inline-block h-4 w-4 bg-white rounded-full shadow-sm transition-transform duration-200 ${on ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </span>
  )
}